import { Category } from '../../model/category/Category';
import i18n from "../../i18n";
import { CategoryInput } from './CategoryInput';

const API_URL = process.env.REACT_APP_PRODUCT_SERVICE_BASE_URL || '';

const CATEGORY_FIELDS = `
    id
    businessId
    name
    description
    color
    imageUrl
    isActive
    isHealthData
    tags
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

export async function getAllCategories(token: string): Promise<Category[]> {
    const data = await gqlPost<{ getAllCategories: Category[] | null }>(
        token,
        `query { getAllCategories { ${CATEGORY_FIELDS} } }`
    );
    return data.getAllCategories ?? [];
}

/** Indexed lookup by businessId — preferred over getAllCategories. */
export async function getCategoriesByBusinessId(
    businessId: number | string,
    token: string
): Promise<Category[]> {
    const data = await gqlPost<{ getCategoriesByBusinessId: Category[] | null }>(
        token,
        `query getCategoriesByBusinessId($businessId: ID!) {
            getCategoriesByBusinessId(businessId: $businessId) { ${CATEGORY_FIELDS} }
        }`,
        { businessId }
    );
    return data.getCategoriesByBusinessId ?? [];
}

export async function getCategoryById(id: string, token: string): Promise<Category | null> {
    const data = await gqlPost<{ getCategoryById: Category | null }>(
        token,
        `query getCategoryById($id: ID!) { getCategoryById(id: $id) { ${CATEGORY_FIELDS} } }`,
        { id }
    );
    return data.getCategoryById ?? null;
}

export async function saveCategory(input: CategoryInput, token: string): Promise<Category> {
    const data = await gqlPost<{ saveCategory: Category }>(
        token,
        `mutation saveCategory($input: CategoryInput!) {
            saveCategory(categoryInput: $input) { ${CATEGORY_FIELDS} }
        }`,
        { input }
    );
    return data.saveCategory;
}

export async function deleteCategory(id: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ deleteCategory: boolean }>(
        token,
        `mutation deleteCategory($id: ID!) { deleteCategory(id: $id) }`,
        { id }
    );
    return !!data.deleteCategory;
}
